import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './styleMobile.css'
import { toast } from "react-toastify";
import { FaArrowLeft, FaEye, FaClock, FaCheckCircle, FaWhatsapp } from "react-icons/fa";
import { MdQrCode2 } from "react-icons/md";
import { trackPedidosPublic, trackPedidoDetailPublic } from "../../services/api";
import { GrInProgress } from "react-icons/gr";
import io from 'socket.io-client';
import ModalPaymentPix from "../../components/ModalPaymentPix";
import ModalPaymentCard from "../../components/ModalPaymentCard";
import { FaCreditCard } from "react-icons/fa";
import getApiUrl from "../../utils/getApiUrl";
import regionConfig from "../../config/regionConfig";

// Adicionar estilos de animação
const animationStyles = `
@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
}

@keyframes progressBar {
    0% { width: 0%; }
    50% { width: 70%; }
    100% { width: 0%; }
}

@keyframes cookingProgress {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.pedido-card {
    animation: fadeInUp 0.5s ease-out;
    transition: all 0.3s ease;
}

.pedido-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}
`;

// Injetar estilos no head
if (typeof document !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = animationStyles;
    document.head.appendChild(styleSheet);
}

const MeusPedidos = () => {
    const { t } = useTranslation('cardapio');
    const { nomeEmpresaForUrl, idEmpresa, slug } = useParams();
    const identifier = slug || idEmpresa;
    const isSlugRoute = !!slug;
    const [searchParams] = useSearchParams();
    const externalId = searchParams.get('external_id');
    const modeParam = searchParams.get('mode');
    const isSalao = modeParam === 'salao';

    // 🔧 FIX: buildUrl com fallback para sessionStorage
    const buildUrl = (subpath) => {
        let base;
        if (isSlugRoute && slug) {
            base = `/c/${slug}/${subpath}`;
        } else if (nomeEmpresaForUrl && idEmpresa) {
            base = `/cardapio/${nomeEmpresaForUrl}/${idEmpresa}/${subpath}`;
        } else {
            // Fallback: buscar do sessionStorage
            try {
                const empresaData = sessionStorage.getItem('sessionCompany');
                if (empresaData) {
                    const empresaParsed = JSON.parse(empresaData);
                    if (empresaParsed?.slug) {
                        base = `/c/${empresaParsed.slug}/${subpath}`;
                    } else if (empresaParsed?.name && empresaParsed?.id_empresa) {
                        const nomeForUrl = empresaParsed.name.toLowerCase().replace(/\s+/g, '-');
                        base = `/cardapio/${nomeForUrl}/${empresaParsed.id_empresa}/${subpath}`;
                    }
                }
            } catch (e) {
                console.error('[MEUS-PEDIDOS] Erro ao construir URL:', e);
            }
            // Último fallback
            if (!base) {
                console.warn('[MEUS-PEDIDOS] buildUrl: Não foi possível construir URL válida para:', subpath);
                base = `/cardapio/${nomeEmpresaForUrl}/${idEmpresa}/${subpath}`;
            }
        }

        const params = new URLSearchParams();
        if (externalId) params.set('external_id', externalId);
        if (isSalao) params.set('mode', 'salao');
        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
    };
    const navigate = useNavigate();

    // ✅ Buscar dados da empresa do sessionStorage (para o CNPJ usado no PIX)
    const empresa = useMemo(() => {
        try {
            const empresaEncrypted = sessionStorage.getItem('sessionCompany');
            if (empresaEncrypted) {
                return JSON.parse(empresaEncrypted);
            }
        } catch (error) {
            console.error('[MEUS-PEDIDOS] Erro ao parsear empresa:', error);
        }
        return null;
    }, []);

    const [pedidos, setPedidos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPedido, setSelectedPedido] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [lastFetch, setLastFetch] = useState(0); // Para controlar cache
    const socketRef = useRef(null); // ✅ Socket.IO para receber atualizações em tempo real

    // ✅ Estado para modal de pagamento PIX
    const [showPixModal, setShowPixModal] = useState(false);

    // ✅ Estado para modal de pagamento CARTÃO
    const [showCardModal, setShowCardModal] = useState(false);

    // ✅ Handler para abrir modal PIX
    const handleOpenPixPayment = () => {
        console.log('[MEUS-PEDIDOS] 📲 Abrindo modal PIX...');
        console.log('[MEUS-PEDIDOS] empresa:', empresa);
        console.log('[MEUS-PEDIDOS] empresa?.cnpj:', empresa?.cnpj);
        console.log('[MEUS-PEDIDOS] selectedPedido:', selectedPedido);
        console.log('[MEUS-PEDIDOS] idEmpresa:', idEmpresa);

        if (!empresa?.cnpj) {
            console.error('[MEUS-PEDIDOS] ❌ CNPJ da empresa não encontrado no sessionStorage!');
            toast.error(t('orders.empresaDataError'));
            return;
        }

        setShowPixModal(true);
    };

    // ✅ Handler quando pagamento PIX é confirmado
    const handlePixPaymentSuccess = (data) => {
        console.log('[MEUS-PEDIDOS] 🎉 Pagamento PIX confirmado:', data);
        toast.success(t('orders.pixConfirmed'));
        setShowPixModal(false);

        // ✅ LIMPAR CARRINHO após pagamento confirmado (igual finalizarPedido.jsx)
        localStorage.removeItem('itensCarrinhoMobile');
        console.log('[MEUS-PEDIDOS] 🛒 Carrinho limpo após pagamento PIX confirmado');

        // Atualizar lista de pedidos
        fetchPedidos(true);
    };

    // ✅ Handler quando pagamento PIX falha
    const handlePixPaymentFailed = (error) => {
        console.log('[MEUS-PEDIDOS] ❌ Pagamento PIX falhou:', error);
        toast.error(t('orders.pixFailed'));
        setShowPixModal(false);
    };

    // ✅ Handler para abrir modal CARTÃO
    const handleOpenCardPayment = () => {
        console.log('[MEUS-PEDIDOS] 💳 Abrindo modal CARTÃO...');
        console.log('[MEUS-PEDIDOS] selectedPedido:', selectedPedido);
        console.log('[MEUS-PEDIDOS] idEmpresa:', idEmpresa);

        if (!empresa?.cnpj) {
            console.error('[MEUS-PEDIDOS] ❌ CNPJ da empresa não encontrado no sessionStorage!');
            toast.error(t('orders.empresaDataError'));
            return;
        }

        setShowCardModal(true);
    };

    // ✅ Handler quando pagamento CARTÃO é confirmado
    const handleCardPaymentSuccess = (data) => {
        console.log('[MEUS-PEDIDOS] 🎉 Pagamento CARTÃO confirmado:', data);
        toast.success(t('orders.cardApproved'));
        setShowCardModal(false);

        // ✅ LIMPAR CARRINHO após pagamento confirmado
        localStorage.removeItem('itensCarrinhoMobile');
        console.log('[MEUS-PEDIDOS] 🛒 Carrinho limpo após pagamento CARTÃO confirmado');

        // Atualizar lista de pedidos
        fetchPedidos(true);
    };

    // ✅ Handler quando pagamento CARTÃO falha
    const handleCardPaymentFailed = (error) => {
        console.log('[MEUS-PEDIDOS] ❌ Pagamento CARTÃO falhou:', error);
        toast.error(t('orders.cardFailed'));
        setShowCardModal(false);
    };

    // 🔧 FIX: handleBack com fallback para sessionStorage
    const handleBack = () => {
        let baseUrl;
        if (isSlugRoute && slug) {
            baseUrl = `/c/${slug}`;
        } else if (nomeEmpresaForUrl && idEmpresa) {
            baseUrl = `/cardapio/${nomeEmpresaForUrl}/${idEmpresa}`;
        } else if (empresa?.slug) {
            baseUrl = `/c/${empresa.slug}`;
        } else if (empresa?.name && empresa?.id_empresa) {
            const nomeForUrl = empresa.name.toLowerCase().replace(/\s+/g, '-');
            baseUrl = `/cardapio/${nomeForUrl}/${empresa.id_empresa}`;
        } else {
            console.warn('[MEUS-PEDIDOS] Usando history.back() como fallback');
            navigate(-1);
            return;
        }

        const params = new URLSearchParams();
        if (externalId) params.set('external_id', externalId);
        if (isSalao) params.set('mode', 'salao');
        const qs = params.toString();
        navigate(qs ? `${baseUrl}?${qs}` : baseUrl);
    };

    // Função para buscar pedidos usando session token
    const fetchPedidos = async (forceRefresh = false) => {
        try {
            // Cache de 25 segundos para evitar consultas excessivas
            const now = Date.now();
            if (!forceRefresh && now - lastFetch < 25000) {
                return;
            }

            const sessionToken = localStorage.getItem('session_token_pedeja');
            
            if (!sessionToken) {
                //toast.warning("Você ainda não fez nenhum pedido");
                setLoading(false);
                return;
            }

            const response = await trackPedidosPublic(sessionToken);
            
            if (response.data && response.data.pedidos) {
                setPedidos(response.data.pedidos);
                setLastFetch(now);
                
                // Salvar no cache para o contador na tela principal
                const cacheData = {
                    pedidos: response.data.pedidos,
                    timestamp: now
                };
                localStorage.setItem(`pedidos_cache_${sessionToken}`, JSON.stringify(cacheData));
            }
        } catch (error) {
            if (error.response?.status === 404) {
                // 404 = nenhum pedido encontrado, não é erro
                setPedidos([]);
            } else {
                toast.error(t('orders.loadError'));
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao buscar pedidos:", error);
            }
        } finally {
            setLoading(false);
        }
    };

    // Buscar detalhes de um pedido específico
    const fetchPedidoDetails = async (idPedido) => {
        try {
            const sessionToken = localStorage.getItem('session_token_pedeja');
            const response = await trackPedidoDetailPublic(sessionToken, idPedido);
            
            if (response.data && response.data.pedido) {
                setSelectedPedido(response.data.pedido);
                setShowDetails(true);
            }
        } catch (error) {
            toast.error(t('orders.detailError'));
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao buscar detalhes:", error);
        }
    };

    // Auto refresh a cada 30 segundos para atualizar status
    useEffect(() => {
        fetchPedidos(true); // Forçar primeira busca

        const interval = setInterval(() => {
            fetchPedidos(); // Busca com cache nas próximas
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    // ✅ WEBSOCKET - Conectar e ouvir eventos de atualização de pedidos em tempo real
    useEffect(() => {
        const sessionToken = localStorage.getItem('session_token_pedeja');

        if (!sessionToken) {
            console.warn('[MEUS-PEDIDOS] Session token não encontrado - WebSocket não conectado');
            return;
        }

        const API_URL = getApiUrl();

        console.log(`[MEUS-PEDIDOS] 📡 Conectando WebSocket para session ${sessionToken.substring(0, 8)} em ${API_URL}`);

        // Criar conexão Socket.IO
        const socket = io(API_URL, {
            withCredentials: true,
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 2000,
        });

        socketRef.current = socket;

        // Evento: Conectado
        socket.on('connect', () => {
            console.log(`[MEUS-PEDIDOS] ✅ WebSocket conectado - Entrando na sala session_${sessionToken.substring(0, 8)}...`);
            socket.emit('joinSessionRoom', { sessionToken });
        });

        // Evento: Pedido atualizado (pagamento confirmado, status mudou, etc)
        socket.on('meus_pedidos_update', (data) => {
            console.log('[MEUS-PEDIDOS] 🔔 Atualização recebida via WebSocket:', data);

            // Atualizar lista de pedidos
            setPedidos(prevPedidos => {
                const pedidoIndex = prevPedidos.findIndex(p => p._id === data.pedido_id);

                if (pedidoIndex >= 0) {
                    // Pedido já existe - atualizar
                    const updatedPedidos = [...prevPedidos];
                    updatedPedidos[pedidoIndex] = {
                        ...updatedPedidos[pedidoIndex],
                        status_pedido: data.status_pedido,
                        payment_confirmed: data.payment_confirmed,
                        payment_confirmed_at: data.payment_confirmed_at,
                        finalizadoAt: data.finalizadoAt
                    };
                    return updatedPedidos;
                } else {
                    // Pedido novo - buscar lista atualizada
                    console.log('[MEUS-PEDIDOS] Pedido não encontrado na lista, buscando atualização...');
                    fetchPedidos(true);
                    return prevPedidos;
                }
            });

            // Mostrar toast de atualização
            if (data.status_pedido === '2') {
                toast.info(t('orders.inProduction'));
            } else if (data.status_pedido === '3') {
                toast.success(t('orders.ready'));
            } else if (data.status_pedido === '4') {
                toast.success(t('orders.finalized'));
            } else if (data.payment_confirmed) {
                toast.success(t('orders.paymentConfirmed'));
            }
        });

        // Evento: Erro de conexão
        socket.on('connect_error', (error) => {
            console.warn('[MEUS-PEDIDOS] ⚠️ Erro de conexão WebSocket:', error.message);
        });

        // Cleanup ao desmontar
        return () => {
            console.log('[MEUS-PEDIDOS] 🔌 Desconectando WebSocket');
            socket.off('meus_pedidos_update');
            socket.disconnect();
            socketRef.current = null;
        };
    }, []); // Executa apenas uma vez ao montar

    // Atualizar selectedPedido quando pedidos forem atualizados (preservando dados existentes)
    useEffect(() => {
        if (selectedPedido) {
            // Encontrar o pedido atualizado na lista
            const pedidoAtualizado = pedidos.find(p => p._id === selectedPedido._id);
            
            if (pedidoAtualizado) {
                // Se pedido existe na lista, atualizar apenas campos específicos
                if (pedidoAtualizado.status_pedido !== selectedPedido.status_pedido) {
                    process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🔄 Atualizando status do pedido:", selectedPedido.status_pedido, "→", pedidoAtualizado.status_pedido);
                    process.env.REACT_APP_ISDEVELOP === 'true' && console.log("💾 Preservando dados:", {
                        nome_cliente: selectedPedido.nome_cliente,
                        celular_cliente: selectedPedido.celular_cliente,
                        qtd_itens: selectedPedido.itens?.length || 0
                    });
                    
                    setSelectedPedido(prev => ({
                        ...prev, // Preserva TODOS os dados existentes
                        status_pedido: pedidoAtualizado.status_pedido, // Atualiza apenas o status
                        finalizadoAt: pedidoAtualizado.finalizadoAt || prev.finalizadoAt // Atualiza finalizadoAt se disponível
                    }));
                }
            } else if (pedidos.length > 0 && selectedPedido.status_pedido !== '4') {
                // Se pedido não existe mais na lista e não está finalizado, provavelmente foi finalizado
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log("🏁 Pedido não encontrado na lista - assumindo finalizado");
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log("💾 Preservando dados do pedido finalizado:", {
                    nome_cliente: selectedPedido.nome_cliente,
                    celular_cliente: selectedPedido.celular_cliente,
                    qtd_itens: selectedPedido.itens?.length || 0
                });
                
                setSelectedPedido(prev => ({
                    ...prev, // Preserva TODOS os dados existentes
                    status_pedido: '4', // Marca como finalizado
                    finalizadoAt: new Date().toISOString() // Adiciona timestamp de finalização
                }));
            }
        }
    }, [pedidos]); // Só depende de pedidos, não de selectedPedido

    // Função para formatar status
    const getStatusInfo = (status, tipoEntrega = 'Retirada') => {
        switch (status) {
            case '0':
                return { label: t('orders.status.awaitingPayment'), color: '#FF9800', icon: <FaClock />, animated: true };
            case '1':
                return { label: t('orders.status.inAnalysis'), color: '#2196f3', icon: <FaEye /> };
            case '2':
                return { label: t('orders.status.inProduction'), color: '#318CD5', icon: <FaClock />, animated: true };
            case '3':
                return {
                    label: tipoEntrega === 'Entrega' ? t('orders.status.onDeliveryRoute') : t('orders.status.readyForPickup'),
                    color: '#4caf50',
                    icon: <FaCheckCircle />
                };
            case '4':
                return { label: t('orders.status.finalized'), color: '#8bc34a', icon: <FaCheckCircle /> };
            default:
                return { label: t('orders.status.unknown'), color: '#757575', icon: <FaClock /> };
        }
    };

    // Função para obter todos os status e seu progresso
    const getStatusProgress = (currentStatus, tipoEntrega = 'Retirada') => {
        // Se status é '0' (aguardando pagamento), usar timeline especial
        if (currentStatus === '0') {
            const allStatuses = [
                { key: '0', label: t('orders.status.awaitingPayment'), description: t('orders.statusDesc.awaitingPayment') },
                { key: '1', label: t('orders.status.inAnalysis'), description: t('orders.statusDesc.inAnalysis') },
                { key: '2', label: t('orders.status.inProduction'), description: t('orders.statusDesc.inProduction') },
                { key: '3', label: tipoEntrega === 'Entrega' ? t('orders.status.onDeliveryRoute') : t('orders.status.readyForPickup'),
                  description: tipoEntrega === 'Entrega' ? t('orders.statusDesc.onDeliveryRoute') : t('orders.statusDesc.readyForPickup') },
                { key: '4', label: t('orders.status.finalized'), description: t('orders.statusDesc.finalized') }
            ];

            return allStatuses.map((status, index) => ({
                ...status,
                completed: false,
                active: index === 0,
                future: index > 0
            }));
        }

        // Timeline normal (sem status 0)
        const allStatuses = [
            { key: '1', label: t('orders.status.inAnalysis'), description: t('orders.statusDesc.inAnalysis') },
            { key: '2', label: t('orders.status.inProduction'), description: t('orders.statusDesc.inProduction') },
            { key: '3', label: tipoEntrega === 'Entrega' ? t('orders.status.onDeliveryRoute') : t('orders.status.readyForPickup'),
              description: tipoEntrega === 'Entrega' ? t('orders.statusDesc.onDeliveryRoute') : t('orders.statusDesc.readyForPickup') },
            { key: '4', label: t('orders.status.finalized'), description: t('orders.statusDesc.finalized') }
        ];

        const currentStatusIndex = allStatuses.findIndex(s => s.key === currentStatus);
        
        return allStatuses.map((status, index) => ({
            ...status,
            completed: index <= currentStatusIndex,
            active: index === currentStatusIndex,
            future: index > currentStatusIndex
        }));
    };

    // Função para formatar data
    const formatDate = (date) => {
        return new Date(date).toLocaleString(regionConfig.currency.locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Componente de Timeline de Status
    const StatusTimeline = ({ currentStatus, createdAt, tipoEntrega }) => {
        const statusProgress = getStatusProgress(currentStatus, tipoEntrega);
        
        return (
            <div style={{
                padding: '20px',
                //background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                borderRadius: '12px',
                //marginBottom: '20px'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '20px',
                    position: 'relative'
                }}>
                    {statusProgress.map((status, index) => {
                        const isLast = index === statusProgress.length - 1;
                        
                        return (
                            <div key={status.key} style={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'relative'
                            }}>
                                {/* Círculo do Status */}
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: status.completed ? '#00D68F' : 
                                               status.active ? '#318CD5' : '#E5E5E5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    zIndex: 2,
                                    position: 'relative',
                                    boxShadow: status.active ? '0 0 20px rgba(49, 140, 213, 0.4)' : 
                                              status.completed ? '0 0 20px rgba(0, 214, 143, 0.4)' : 'none',
                                    animation: status.active ? 'pulse 2s infinite' : 'none'
                                }}>
                                    {status.completed ? '✓' : index + 1}
                                </div>
                                
                                {/* Linha de Conexão */}
                                {!isLast && (
                                    <div style={{
                                        position: 'absolute',
                                        left: 'calc(50% + 20px)',
                                        right: 'calc(-50% + 20px)',
                                        height: '4px',
                                        top: '18px',
                                        zIndex: 1
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            background: '#E5E5E5',
                                            borderRadius: '2px'
                                        }}>
                                            <div style={{
                                                height: '100%',
                                                background: status.completed ? 
                                                    'linear-gradient(90deg, #00D68F, #00A86B)' : '#E5E5E5',
                                                borderRadius: '2px',
                                                width: status.completed ? '100%' : '0%',
                                                transition: 'width 1s ease-in-out',
                                                animation: status.active && !status.completed ? 
                                                    'progressBar 2s ease-in-out infinite' : 'none'
                                            }} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                
                {/* Labels dos Status */}
                <div style={{
                    display: 'flex',
                    marginTop: '10px'
                }}>
                    {statusProgress.map((status, index) => (
                        <div key={`label-${status.key}`} style={{
                            flex: 1,
                            fontSize: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                fontWeight: status.active ? '600' : '500',
                                color: status.completed ? '#00A86B' : 
                                       status.active ? '#318CD5' : '#666',
                                marginBottom: '4px',
                                lineHeight: '1.2'
                            }}>
                                {status.label}
                            </div>
                            <div style={{
                                color: '#999',
                                fontSize: '10px',
                                lineHeight: '1.2'
                            }}>
                                {index === 0 ? formatDate(createdAt) :
                                 status.completed ? t('orders.status.completed') :
                                 status.active ? t('orders.status.inProgress') : t('orders.status.waiting')}
                            </div>
                        </div>
                    ))}
                </div>
                
                {/* Barra de progresso animada para status ativo */}
                {statusProgress.find(s => s.active && s.key === '2') && (
                    <div style={{
                        marginTop: '15px',
                        padding: '12px',
                        background: 'rgba(49, 140, 213, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(49, 140, 213, 0.2)'
                    }}>
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: '#318CD5',
                            marginBottom: '8px'
                        }}>
                            <GrInProgress /> {t('orders.beingPrepared')}
                        </div>
                        <div style={{
                            width: '100%',
                            height: '6px',
                            background: 'rgba(79, 172, 254, 0.2)',
                            borderRadius: '3px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                height: '100%',
                                background: 'linear-gradient(90deg, #318CD5, #4FACFE)',
                                borderRadius: '3px',
                                animation: 'cookingProgress 3s ease-in-out infinite'
                            }} />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Função para formatar valor
    const formatCurrency = (value) => {
        return new Intl.NumberFormat(regionConfig.currency.locale, {
            style: 'currency',
            currency: regionConfig.currency.code
        }).format(value);
    };

    if (loading) {
        return (
            <div className="pageCarrinhoStyle">
                <div className="cabecalhoCarrinho" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '15px 20px',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <FaArrowLeft 
                        onClick={handleBack} 
                        style={{ 
                            fontSize: '20px', 
                            color: '#333', 
                            cursor: 'pointer' 
                        }} 
                    />
                    <h5 style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        color: '#333' 
                    }}>
                        {t('orders.title')}
                    </h5>
                    <div style={{ width: '20px' }}></div>
                </div>
                <div className="loading-container" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '50vh'
                }}>
                    <div>{t('orders.loadingOrders')}</div>
                </div>
            </div>
        );
    }

    if (showDetails && selectedPedido) {
        return (
            <>
            <div className="pageCarrinhoStyle">
                <div className="cabecalhoCarrinho" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '15px 20px',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <FaArrowLeft 
                        onClick={() => setShowDetails(false)} 
                        style={{ 
                            fontSize: '20px', 
                            color: '#333', 
                            cursor: 'pointer' 
                        }} 
                    />
                    <h5 style={{ 
                        margin: 0, 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#333',
                        textAlign: 'center'
                    }}>
                        {t('orders.orderDetail', { id: selectedPedido.id_pedido_counter })}
                    </h5>
                    <div style={{ width: '20px' }}></div>
                </div>
                
                <div className="pedido-details" style={{ padding: '20px' }}>
                    {/* Timeline de Status Profissional */}
                    <StatusTimeline
                        currentStatus={selectedPedido.status_pedido}
                        createdAt={selectedPedido.createdAt}
                        tipoEntrega={selectedPedido.entrega?.tipo_entrega || 'Retirada'}
                    />

                    {/* Acompanhar pelo WhatsApp: a loja so consegue avisar (sem risco de
                        restricao) quem ja escreveu pra ela -- o cliente abre a conversa daqui */}
                    {empresa?.whatsapp_numero && selectedPedido.status_pedido !== '4' && (
                        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                            <a
                                href={`https://wa.me/${empresa.whatsapp_numero}?text=${encodeURIComponent(
                                    t('orders.trackMessage', { id: selectedPedido.id_pedido_counter || selectedPedido.id_pedido })
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    background: '#25D366',
                                    color: 'white',
                                    padding: '14px 16px',
                                    borderRadius: '12px',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    fontSize: '15px',
                                    boxShadow: '0 4px 14px rgba(37, 211, 102, 0.35)'
                                }}
                            >
                                <FaWhatsapp style={{ fontSize: '20px' }} />
                                {t('orders.trackOnWhatsapp')}
                            </a>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                                {t('orders.trackOnWhatsappHint')}
                            </div>
                        </div>
                    )}

                    {/* Card Informativo baseado no status e tipo de entrega */}
                    {selectedPedido.status_pedido === '3' && (
                        <div style={{
                            background: selectedPedido.entrega?.tipo_entrega === 'Entrega' 
                                ? 'linear-gradient(135deg, #4CAF50, #66BB6A)' 
                                : 'linear-gradient(135deg, #318CD5, #4FACFE)',
                            color: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            marginBottom: '20px',
                            textAlign: 'center',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Background pattern sutil */}
                            <div style={{
                                position: 'absolute',
                                top: '-50%',
                                right: '-50%',
                                width: '200%',
                                height: '200%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
                                backgroundSize: '20px 20px',
                                opacity: 0.3,
                                pointerEvents: 'none'
                            }} />
                            
                            <div style={{ position: 'relative', zIndex: 1 }}>                                
                                
                                <h4 style={{
                                    margin: '0 0 8px 0',
                                    fontSize: '18px',
                                    fontWeight: '600'
                                }}>
                                    {selectedPedido.entrega?.tipo_entrega === 'Entrega'
                                        ? t('orders.deliveryOnWay')
                                        : t('orders.readyForPickup')}
                                </h4>
                                
                                <p style={{
                                    margin: '0 0 2px 0',
                                    fontSize: '14px',
                                    opacity: 0.95,
                                    lineHeight: '1.4'
                                }}>
                                    {selectedPedido.entrega?.tipo_entrega === 'Entrega'
                                        ? t('orders.deliveryOnWayMsg')
                                        : t('orders.readyForPickupMsg')}
                                </p>
                                
                                {selectedPedido.entrega?.tipo_entrega === 'Entrega' ? (
                                    <div style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        marginTop: '10px'
                                    }}>
                                        <strong>{t('orders.deliveryAddressLabel')}</strong>
                                        <br />
                                        {selectedPedido.entrega?.endereco?.rua && selectedPedido.entrega?.endereco?.numero && 
                                            `${selectedPedido.entrega.endereco.rua}, ${selectedPedido.entrega.endereco.numero}`
                                        }
                                        {selectedPedido.entrega?.endereco?.bairro && 
                                            ` - ${selectedPedido.entrega.endereco.bairro}`
                                        }
                                    </div>
                                ) : (
                                    <div style={{
                                        background: 'rgba(255,255,255,0.2)',
                                        padding: '10px',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        marginTop: '10px'
                                    }}>
                                        <strong>{t('orders.timeLabel')}</strong> {t('orders.availableForPickup')}
                                        <br />
                                        <strong>{t('orders.questionsLabel')}</strong> {t('orders.contactStore')}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ✅ Card para pagamento PIX pendente (status '0' e pagamento PIX Online) */}
                    {selectedPedido.status_pedido === '0' && selectedPedido.tipo_pagamento?.toLowerCase().includes('pix') && (
                        <div style={{
                            background: 'linear-gradient(135deg, #FF9800, #FFB74D)',
                            color: 'white',
                            padding: '20px',
                            borderRadius: '12px',
                            marginBottom: '20px',
                            textAlign: 'center',
                            boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* Background pattern sutil */}
                            <div style={{
                                position: 'absolute',
                                top: '-50%',
                                right: '-50%',
                                width: '200%',
                                height: '200%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
                                backgroundSize: '20px 20px',
                                opacity: 0.3,
                                pointerEvents: 'none'
                            }} />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <h4 style={{
                                    margin: '0 0 8px 0',
                                    fontSize: '18px',
                                    fontWeight: '600'
                                }}>
                                    {t('orders.awaitingPixPayment')}
                                </h4>

                                <p style={{
                                    margin: '0 0 15px 0',
                                    fontSize: '14px',
                                    opacity: 0.95,
                                    lineHeight: '1.4'
                                }}>
                                    {t('orders.awaitingPixMsg')}
                                </p>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenPixPayment();
                                    }}
                                    style={{
                                        background: 'white',
                                        color: '#FF9800',
                                        border: 'none',
                                        padding: '12px 24px',
                                        borderRadius: '25px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                    }}
                                    onMouseOver={(e) => {
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.transform = 'scale(1)';
                                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                                    }}
                                >
                                    <MdQrCode2 size={20} />
                                    {t('orders.payWithPix')}
                                </button>

                                <div style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                    marginTop: '15px'
                                }}>
                                    <strong>{t('orders.pixTip')}</strong> {t('orders.pixAutoConfirm')}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ✅ Card de Pagamento com Cartão - Apenas para pedidos pendentes de pagamento com cartão */}
                    {(selectedPedido.status_pedido === '0' || selectedPedido.status_pedido === 0) &&
                     (selectedPedido.tipo_pagamento?.toLowerCase().includes('cartao') ||
                      selectedPedido.tipo_pagamento?.toLowerCase().includes('cartão') ||
                      selectedPedido.tipo_pagamento === 'CartaoOnline' ||
                      selectedPedido.tipo_pagamento === 'Cartão') && (
                        <div style={{
                            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                            borderRadius: '16px',
                            padding: '20px',
                            marginBottom: '20px',
                            boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
                            color: 'white',
                            animation: 'fadeIn 0.3s ease-in-out'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '15px'
                            }}>
                                <div style={{
                                    width: '50px',
                                    height: '50px',
                                    background: 'rgba(255,255,255,0.2)',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '24px'
                                }}>
                                    <FaCreditCard />
                                </div>
                                <div>
                                    <h5 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                                        {t('orders.cardPaymentPending')}
                                    </h5>
                                    <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                                        {t('orders.completeCardPayment')}
                                    </p>
                                </div>
                            </div>

                            <div style={{
                                background: 'rgba(255,255,255,0.15)',
                                borderRadius: '12px',
                                padding: '15px',
                                marginBottom: '15px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '8px'
                                }}>
                                    <span style={{ opacity: 0.9 }}>{t('orders.amountToPay')}</span>
                                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>
                                        {formatCurrency(selectedPedido.valor_total)}
                                    </span>
                                </div>
                                <div style={{
                                    fontSize: '12px',
                                    opacity: 0.7
                                }}>
                                    {t('orders.orderNumber', { id: selectedPedido.id_pedido_counter || selectedPedido.id_pedido })}
                                </div>
                            </div>

                            <button
                                onClick={handleOpenCardPayment}
                                style={{
                                    width: '100%',
                                    padding: '15px 20px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    background: 'white',
                                    color: '#6366F1',
                                    border: 'none',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    height: '50px',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                                }}
                            >
                                <FaCreditCard size={20} />
                                {t('orders.finishCardPayment')}
                            </button>

                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                marginTop: '15px',
                                fontSize: '13px',
                                opacity: 0.9
                            }}>
                                <span>🔒</span>
                                <span>{t('orders.securePayment')}</span>
                            </div>

                            <div style={{
                                textAlign: 'center',
                                background: 'rgba(255,255,255,0.2)',
                                padding: '10px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                marginTop: '15px'
                            }}>
                                <strong>{t('orders.pixTip')}</strong> {t('orders.pixAutoConfirm')}
                            </div>
                        </div>
                    )}

                    {/* Informações do Pedido */}
                    <div style={{ 
                        background: 'white', 
                        padding: '20px', 
                        borderRadius: '12px', 
                        marginBottom: '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #f0f0f0'
                    }}>
                        <h6 style={{ 
                            margin: '0 0 15px 0',
                            color: '#333',
                            fontSize: '16px',
                            fontWeight: '600'
                        }}>
                            {t('orders.orderInfo')}
                        </h6>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                                <strong style={{ color: '#666', fontSize: '14px' }}>{t('orders.client')}</strong>
                                <div style={{ color: '#333', fontSize: '15px' }}>{selectedPedido.nome_cliente}</div>
                            </div>
                            <div>
                                <strong style={{ color: '#666', fontSize: '14px' }}>{t('orders.phone')}</strong>
                                <div style={{ color: '#333', fontSize: '15px' }}>{selectedPedido.celular_cliente}</div>
                            </div>
                            <div>
                                <strong style={{ color: '#666', fontSize: '14px' }}>{t('orders.date')}</strong>
                                <div style={{ color: '#333', fontSize: '15px' }}>{formatDate(selectedPedido.createdAt)}</div>
                            </div>
                            <div>
                                <strong style={{ color: '#666', fontSize: '14px' }}>{t('orders.payment')}</strong>
                                <div style={{ color: '#333', fontSize: '15px' }}>{selectedPedido.tipo_pagamento}</div>
                            </div>
                        </div>
                        
                        <div style={{ 
                            marginTop: '15px', 
                            paddingTop: '15px', 
                            borderTop: '1px solid #f0f0f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <strong style={{ color: '#666', fontSize: '14px' }}>{t('orders.orderTotal')}</strong>
                            <div style={{ 
                                color: '#00A86B', 
                                fontSize: '20px', 
                                fontWeight: 'bold'
                            }}>
                                {formatCurrency(selectedPedido.valor_total)}
                            </div>
                        </div>
                    </div>

                    <h6>{t('orders.orderItems')}</h6>
                    <div style={{ marginBottom: '20px' }}>
                        {selectedPedido.itens && Array.isArray(selectedPedido.itens) ? (
                            selectedPedido.itens.map((item, index) => (
                                <div key={`${item.nomeItem}-${index}`} style={{ 
                                    border: '1px solid #ddd', 
                                    padding: '15px', 
                                    marginBottom: '10px',
                                    borderRadius: '5px'
                                }}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        marginBottom: '10px'
                                    }}>
                                        <div style={{ fontWeight: 'bold' }}>
                                            {parseInt(item.quantidade)}x {item.nomeItem}
                                        </div>
                                        <div style={{ fontWeight: 'bold' }}>
                                            {formatCurrency(item.valor * item.quantidade)}
                                        </div>
                                    </div>
                                    
                                    {/* Renderizar adicionais */}
                                    {item.grupo_adicionais && item.grupo_adicionais.map((grupo, idx) => (
                                        <div key={`${item.nomeItem}-${grupo.grupoTitle}-${idx}`} style={{
                                            marginLeft: '10px',
                                            marginBottom: '8px',
                                            padding: '8px',
                                            backgroundColor: '#f9f9f9',
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ fontWeight: '500', marginBottom: '5px', color: '#555' }}>
                                                {grupo.grupoTitle}
                                            </div>
                                            {Array.isArray(grupo.adicionais) && grupo.adicionais.map((adicional, adIdx) => (
                                                <div key={`${item.nomeItem}-${grupo.grupoTitle}-${adicional.title}-${adIdx}`} 
                                                     style={{
                                                         display: 'flex',
                                                         justifyContent: 'space-between',
                                                         fontSize: '0.9em',
                                                         color: '#666',
                                                         marginBottom: '3px'
                                                     }}>
                                                    <span>
                                                        {grupo.calcular_maior_valor || grupo.calcular_media
                                                            ? adicional.quantity > grupo.adicionais.length
                                                                ? `${grupo.adicionais.length}/${grupo.adicionais.length}`
                                                                : `${adicional.quantity}/${grupo.adicionais.length}`
                                                            : `${parseInt(adicional.quantity)}x`} {adicional.title}
                                                    </span>
                                                    <span>
                                                        {formatCurrency(
                                                            grupo.calcular_media
                                                                ? (grupo.adicionais.reduce((acc, subAdicional) => acc + (subAdicional.price * subAdicional.quantity), 0) /
                                                                   grupo.adicionais.reduce((acc, subAdicional) => acc + subAdicional.quantity, 0) /
                                                                   grupo.adicionais.length)
                                                                : grupo.calcular_maior_valor
                                                                    ? (adicional.price === Math.max(...grupo.adicionais.map(subAdicional => subAdicional.price))
                                                                        ? Math.max(...grupo.adicionais.map(subAdicional => subAdicional.price))
                                                                        : 0)
                                                                    : (adicional.price * adicional.quantity)
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                    
                                    {/* Observação do item */}
                                    {item.obs_item && item.obs_item !== "" && (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '8px',
                                            backgroundColor: '#fff3cd',
                                            borderRadius: '4px',
                                            fontSize: '0.9em',
                                            color: '#856404'
                                        }}>
                                            <strong>{t('orders.observation')}</strong> {item.obs_item}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                                {t('orders.noItemsFound')}
                            </div>
                        )}
                    </div>

                    {selectedPedido.entrega && (
                        <div>
                            <h6>{t('orders.deliveryInfo')}</h6>
                            <div style={{ 
                                background: '#f5f5f5', 
                                padding: '15px', 
                                borderRadius: '8px' 
                            }}>
                                <p><strong>{t('orders.type')}</strong> {selectedPedido.entrega.tipo_entrega}</p>
                                {selectedPedido.entrega.endereco && (
                                    <div style={{ marginBottom: '10px' }}>
                                        <strong>{t('orders.addressLabel')}</strong>
                                        <div style={{ marginTop: '5px', lineHeight: '1.4' }}>
                                            {typeof selectedPedido.entrega.endereco === 'string' ? (
                                                selectedPedido.entrega.endereco
                                            ) : (
                                                <>
                                                    {selectedPedido.entrega.endereco.rua && selectedPedido.entrega.endereco.numero && 
                                                        `${selectedPedido.entrega.endereco.rua}, ${selectedPedido.entrega.endereco.numero}`
                                                    }
                                                    {selectedPedido.entrega.endereco.complemento && 
                                                        ` - ${selectedPedido.entrega.endereco.complemento}`
                                                    }
                                                    <br />
                                                    {selectedPedido.entrega.endereco.bairro && 
                                                        `${selectedPedido.entrega.endereco.bairro}`
                                                    }
                                                    {selectedPedido.entrega.endereco.cidade && selectedPedido.entrega.endereco.estado &&
                                                        `, ${selectedPedido.entrega.endereco.cidade} - ${selectedPedido.entrega.endereco.estado}`
                                                    }
                                                    {selectedPedido.entrega.endereco.cep && 
                                                        ` CEP: ${selectedPedido.entrega.endereco.cep}`
                                                    }
                                                    {selectedPedido.entrega.endereco.referencia && (
                                                        <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                                                            <strong>{t('orders.referenceLabel')}</strong> {selectedPedido.entrega.endereco.referencia}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {selectedPedido.entrega.valor_entrega && (
                                    <p><strong>{t('orders.deliveryFee')}</strong> {formatCurrency(selectedPedido.entrega.valor_entrega)}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

            </div>

            {/* ✅ Modal de Pagamento PIX - FORA do container para evitar problemas de z-index */}
            {showPixModal && selectedPedido && (
                <ModalPaymentPix
                    isOpen={showPixModal}
                    onClose={() => setShowPixModal(false)}
                    pedidoId={selectedPedido._id}
                    amount={selectedPedido.valor_total}
                    idEmpresa={idEmpresa}
                    customer={{
                        name: selectedPedido.nome_cliente,
                        phone: selectedPedido.celular_cliente?.replace(/\D/g, ''),
                        document: '' // CPF/CNPJ do titular é coletado separadamente no modal
                    }}
                    onPaymentSuccess={handlePixPaymentSuccess}
                    onPaymentFailed={handlePixPaymentFailed}
                />
            )}

            {/* ✅ Modal de Pagamento com Cartão - FORA do container para evitar problemas de z-index */}
            {showCardModal && selectedPedido && (
                <ModalPaymentCard
                    isOpen={showCardModal}
                    onClose={() => setShowCardModal(false)}
                    pedidoId={selectedPedido._id}
                    amount={selectedPedido.valor_total}
                    idEmpresa={idEmpresa}
                    customer={{
                        name: selectedPedido.nome_cliente,
                        phone: selectedPedido.celular_cliente?.replace(/\D/g, ''),
                        document: '' // CPF/CNPJ do titular é coletado separadamente no modal
                    }}
                    deliveryAddress={selectedPedido.endereco_cliente ? {
                        street: selectedPedido.endereco_cliente.logradouro || '',
                        number: selectedPedido.endereco_cliente.numero || '',
                        complement: selectedPedido.endereco_cliente.complemento || '',
                        neighborhood: selectedPedido.endereco_cliente.bairro || '',
                        city: selectedPedido.endereco_cliente.cidade || '',
                        state: selectedPedido.endereco_cliente.estado || '',
                        country: 'BR',
                        postalCode: selectedPedido.endereco_cliente.cep?.replace(/\D/g, '') || ''
                    } : null}
                    onPaymentSuccess={handleCardPaymentSuccess}
                    onPaymentFailed={handleCardPaymentFailed}
                />
            )}
            </>
        );
    }

    return (
        <div className="pageCarrinhoStyle">
            <div className="cabecalhoCarrinho" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '15px 20px',
                borderBottom: '1px solid #f0f0f0'
            }}>
                <FaArrowLeft 
                    onClick={handleBack} 
                    style={{ 
                        fontSize: '20px', 
                        color: '#333', 
                        cursor: 'pointer' 
                    }} 
                />
                <h5 style={{ 
                    margin: 0, 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: '#333' 
                }}>
                    {t('orders.title')}
                </h5>
                <div style={{ width: '20px' }}></div>
            </div>

            {pedidos.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px',
                    color: '#666' 
                }}>
                    <h6>{t('orders.noOrders')}</h6>
                    <p>{t('orders.noOrdersHint')}</p>
                </div>
            ) : (
                <div className="pedidos-list" style={{ padding: '20px' }}>
                    <div style={{ 
                        marginBottom: '20px', 
                        fontSize: '0.9em', 
                        color: '#666',
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #318CD5, #4FACFE)',
                        color: 'white',
                        padding: '12px',
                        borderRadius: '8px'
                    }}>
                        <p style={{ margin: '0 0 5px 0' }}>{t('orders.autoUpdates')}</p>
                        <p style={{ margin: 0, fontSize: '0.8em', opacity: 0.9 }}>
                            {t('orders.lastUpdate', { time: new Date().toLocaleTimeString() })}
                        </p>
                    </div>

                    {pedidos.map((pedido, index) => {
                        const statusInfo = getStatusInfo(pedido.status_pedido, pedido.entrega?.tipo_entrega);
                        //const isRecent = (new Date() - new Date(pedido.createdAt)) < (24 * 60 * 60 * 1000); // 24h
                        
                        return (
                            <div
                                key={pedido._id}
                                className="pedido-card"
                                onClick={() => fetchPedidoDetails(pedido.id_pedido_counter)}
                                style={{
                                    background: 'white',
                                    borderRadius: '12px',
                                    padding: '20px',
                                    marginBottom: '15px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                    border: pedido.status_pedido === '0' ? '2px solid #FF9800' :
                                           pedido.status_pedido === '2' ? '2px solid #318CD5' : '1px solid #f0f0f0',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    animationDelay: `${index * 0.1}s`
                                }}
                            >
                                {/* Indicador de status animado - Aguardando pagamento */}
                                {pedido.status_pedido === '0' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: 'linear-gradient(90deg, #FF9800, #FFB74D)',
                                        animation: 'cookingProgress 2s ease-in-out infinite'
                                    }} />
                                )}
                                {/* Indicador de status animado - Em produção */}
                                {pedido.status_pedido === '2' && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: 'linear-gradient(90deg, #318CD5, #4FACFE)',
                                        animation: 'cookingProgress 2s ease-in-out infinite'
                                    }} />
                                )}
                                
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'flex-start',
                                    marginBottom: '15px'
                                }}>
                                    <div>
                                        <h6 style={{ 
                                            margin: '0 0 8px 0',
                                            fontSize: '18px',
                                            fontWeight: '600',
                                            color: '#333'
                                        }}>
                                            {t('orders.orderNumber', { id: pedido.id_pedido_counter })}
                                        </h6>
                                        <div style={{ 
                                            fontSize: '14px',
                                            color: '#666',
                                            marginBottom: '4px'
                                        }}>
                                            {formatDate(pedido.createdAt)}
                                        </div>
                                        <div style={{ 
                                            fontSize: '14px',
                                            color: '#888'
                                        }}>
                                            {pedido.tipo_pagamento}
                                        </div>
                                    </div>
                                    
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{
                                            background: pedido.status_pedido === '0' ? '#FF9800' :
                                                       pedido.status_pedido === '2' ? '#318CD5' :
                                                       pedido.status_pedido === '4' ? '#00D68F' :
                                                       pedido.status_pedido === '3' ? '#4caf50' :
                                                       pedido.status_pedido === '1' ? '#2196f3' : '#757575',
                                            color: 'white',
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            marginBottom: '8px',
                                            animation: (pedido.status_pedido === '2' || pedido.status_pedido === '0') ? 'pulse 2s infinite' : 'none'
                                        }}>
                                            {statusInfo.icon}
                                            {statusInfo.label}
                                        </div>
                                        <div style={{ 
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: '#00A86B'
                                        }}>
                                            {formatCurrency(pedido.valor_total)}
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Barra de progresso mini para pedidos em andamento */}
                                {pedido.status_pedido === '2' && (
                                    <div style={{
                                        width: '100%',
                                        height: '3px',
                                        background: 'rgba(79, 172, 254, 0.2)',
                                        borderRadius: '2px',
                                        marginBottom: '12px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            background: 'linear-gradient(90deg, #318CD5, #4FACFE)',
                                            borderRadius: '2px',
                                            animation: 'cookingProgress 3s ease-in-out infinite'
                                        }} />
                                    </div>
                                )}
                                
                                <div style={{ 
                                    fontSize: '14px',
                                    color: '#999',
                                    textAlign: 'center',
                                    padding: '8px',
                                    background: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px dashed #ddd'
                                }}>
                                    {t('orders.tapForDetails')}
                                </div>
                                
                                {/*isRecent && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '10px',
                                        left: '10px',
                                        background: '#FF6B35',
                                        color: 'white',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: '600'
                                    }}>
                                        NOVO
                                    </div>
                                )*/}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ✅ Modal de Pagamento PIX */}
            {showPixModal && selectedPedido && (
                <ModalPaymentPix
                    isOpen={showPixModal}
                    onClose={() => setShowPixModal(false)}
                    pedidoId={selectedPedido._id}
                    amount={selectedPedido.valor_total}
                    idEmpresa={idEmpresa}
                    customer={{
                        name: selectedPedido.nome_cliente,
                        phone: selectedPedido.celular_cliente?.replace(/\D/g, ''),
                        document: '' // CPF/CNPJ do titular é coletado separadamente no modal
                    }}
                    onPaymentSuccess={handlePixPaymentSuccess}
                    onPaymentFailed={handlePixPaymentFailed}
                />
            )}

            {/* ✅ Modal de Pagamento com Cartão */}
            {showCardModal && selectedPedido && (
                <ModalPaymentCard
                    isOpen={showCardModal}
                    onClose={() => setShowCardModal(false)}
                    pedidoId={selectedPedido._id}
                    amount={selectedPedido.valor_total}
                    idEmpresa={idEmpresa}
                    customer={{
                        name: selectedPedido.nome_cliente,
                        phone: selectedPedido.celular_cliente?.replace(/\D/g, ''),
                        document: '' // CPF/CNPJ do titular é coletado separadamente no modal
                    }}
                    deliveryAddress={selectedPedido.endereco_cliente ? {
                        street: selectedPedido.endereco_cliente.logradouro || '',
                        number: selectedPedido.endereco_cliente.numero || '',
                        complement: selectedPedido.endereco_cliente.complemento || '',
                        neighborhood: selectedPedido.endereco_cliente.bairro || '',
                        city: selectedPedido.endereco_cliente.cidade || '',
                        state: selectedPedido.endereco_cliente.estado || '',
                        country: 'BR',
                        postalCode: selectedPedido.endereco_cliente.cep?.replace(/\D/g, '') || ''
                    } : null}
                    onPaymentSuccess={handleCardPaymentSuccess}
                    onPaymentFailed={handleCardPaymentFailed}
                />
            )}
        </div>
    );
};

export default MeusPedidos;